import { useState, useEffect } from 'react';
import { createMandataire, updateMandataire } from '../../../service/api/AssociationService';
import { toast } from 'sonner';

const AssociationForm = ({ account = {}, accountId, onClose, onSuccess, editingMandataire }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState(null);
  const [cni, setCni] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Pré-remplissage pour édition
  useEffect(() => {
    if (editingMandataire) {
      setFirstName(editingMandataire.firstName || '');
      setLastName(editingMandataire.lastName || '');
      setTelephone(editingMandataire.telephone || '');
      setEmail(editingMandataire.email || ''); // Pré-remplit l'email uniquement pour le frontend
      setSignature(editingMandataire.signature || null); // Pré-remplit la signature existante
      setCni(editingMandataire.cni || null); // Pré-remplit la CNI existante
      setIsEditing(true);
    } else {
      setFirstName('');
      setLastName('');
      setTelephone('');
      setEmail('');
      setSignature(null);
      setCni(null);
      setIsEditing(false);
    }
  }, [editingMandataire]);

  // Fonction de validation en temps réel
  const validateField = (name, value) => {
    let error = '';
    if (!value.trim()) {
      error = `${name === 'firstName' ? 'Le prénom' : name === 'lastName' ? 'Le nom' : name === 'telephone' ? 'Le numéro de téléphone' : 'L’email'} est requis.`;
    }
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation des champs obligatoires
    const errors = {
      firstName: firstName.trim() ? '' : 'Le prénom est requis.',
      lastName: lastName.trim() ? '' : 'Le nom est requis.',
      telephone: telephone.trim() ? '' : 'Le numéro de téléphone est requis.',
      ...(isEditing
        ? {} // Pas de validation pour email, signature et cni en mode édition
        : {
            email: email.trim() ? '' : 'L’email est requis.',
            signature: signature ? '' : 'La signature est requise.',
            cni: cni ? '' : 'La CNI est requise.',
          }),
    };

    if (Object.values(errors).some((err) => err)) {
      setFormErrors(errors);
      return;
    }

    try {
      if (isEditing) {
        // Préparer les données pour l'édition
        const updatedData = {
          id: editingMandataire?.id || 0, // ID du mandataire
          accountId: accountId, // ID du compte
          firstName: firstName,
          lastName: lastName,
          telephone: telephone,
        };

        // console.log("Données envoyées pour l'édition :", updatedData); // Log pour débogage
        await updateMandataire(editingMandataire.id, updatedData); // Appel à l'API pour mise à jour
        toast.success('Mandataire mis à jour avec succès');
      } else {
        // Préparer les données pour la création
        const formData = new FormData();
        formData.append('AccountId', accountId);
        formData.append('FirstName', firstName);
        formData.append('LastName', lastName);
        formData.append('Telephone', telephone);
        formData.append('Email', email);
        formData.append('Signature', signature);
        formData.append('Cni', cni);

        // console.log("Données envoyées pour la création :", Object.fromEntries(formData.entries())); // Log pour débogage
        await createMandataire(formData); // Appel à l'API pour création
        toast.success('Mandataire créé avec succès');
      }

      onSuccess();
      onClose();
    } catch (err) {
      // console.error('Erreur lors de la soumission :', err);
      toast.error('Une erreur est survenue, veuillez réessayer.');
    }
  };

  return (
    <div className="modal fade show d-block" >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditing ? 'Modifier' : 'Ajouter'} un mandataire pour{' '}
              <strong className="text-primary">{account?.companyName || 'ce compte'}</strong>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="firstName">Prénom</label>
                <input
                  type="text"
                  id="firstName"
                  className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`}
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    validateField('firstName', e.target.value);
                  }}
                />
                {formErrors.firstName && <div className="invalid-feedback">{formErrors.firstName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Nom</label>
                <input
                  type="text"
                  id="lastName"
                  className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`}
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    validateField('lastName', e.target.value);
                  }}
                />
                {formErrors.lastName && <div className="invalid-feedback">{formErrors.lastName}</div>}
              </div>

              <div className="form-group">
                <label htmlFor="telephone">Téléphone</label>
                <input
                  type="text"
                  id="telephone"
                  className={`form-control ${formErrors.telephone ? 'is-invalid' : ''}`}
                  value={telephone}
                  onChange={(e) => {
                    setTelephone(e.target.value);
                    validateField('telephone', e.target.value);
                  }}
                />
                {formErrors.telephone && <div className="invalid-feedback">{formErrors.telephone}</div>}
              </div>

              {!isEditing && (
                <>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        validateField('email', e.target.value);
                      }}
                    />
                    {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="signature">Signature</label>
                    <input
                      type="file"
                      id="signature"
                      className={`form-control ${formErrors.signature ? 'is-invalid' : ''}`}
                      onChange={(e) => setSignature(e.target.files[0])}
                    />
                    {formErrors.signature && <div className="invalid-feedback">{formErrors.signature}</div>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="cni">CNI</label>
                    <input
                      type="file"
                      id="cni"
                      className={`form-control ${formErrors.cni ? 'is-invalid' : ''}`}
                      onChange={(e) => setCni(e.target.files[0])}
                    />
                    {formErrors.cni && <div className="invalid-feedback">{formErrors.cni}</div>}
                  </div>
                </>
              )}
              <div className="d-flex justify-content-between mt-3 ">

              <button type="submit" className="btn btn-primary mt-3">
                {isEditing ? 'Modifier' : 'Ajouter'}
              </button>
              <button type="button" className="btn btn-secondary " onClick={onClose}>
              Fermer
                </button>
                </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssociationForm;